import { isEmbedded } from "./embed";

// Context sent with each tracking lookup (?ctx= query parameter) so the
// backend can store where the lookup came from. The IP itself is read on the
// server; nothing here identifies a person on its own.

const randomId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// Browser storage can be blocked inside an iframe (third-party storage,
// Safari / private mode), so every access is guarded with an in-memory fallback.
const memory = {};
const storedId = (storage, key) => {
  try {
    const store = window[storage];
    let id = store.getItem(key);
    if (!id) {
      id = randomId();
      store.setItem(key, id);
    }
    return id;
  } catch (e) {
    if (!memory[key]) memory[key] = randomId();
    return memory[key];
  }
};

/** Page that embeds the tracker (only meaningful inside an iframe). */
const getParentUrl = () => {
  if (!isEmbedded()) return undefined;
  // document.referrer is the embedding page; ancestorOrigins (Chrome/Safari)
  // still gives its origin when the referrer policy strips the referrer.
  return document.referrer || window.location.ancestorOrigins?.[0] || undefined;
};

export const getClientContext = () => {
  let timezone;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    timezone = undefined;
  }
  return {
    embedded: isEmbedded(),
    parentUrl: getParentUrl(),
    pageUrl: window.location.href,
    referrer: document.referrer || undefined,
    visitorId: storedId("localStorage", "smile_tracking_visitor"),
    sessionId: storedId("sessionStorage", "smile_tracking_session"),
    language: navigator.language,
    timezone,
    screen: window.screen ? `${window.screen.width}x${window.screen.height}` : undefined,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    platform: navigator.userAgentData?.platform || navigator.platform,
  };
};

/**
 * Context as a URL query parameter (?ctx=...). A query parameter keeps the
 * request a "simple" GET (no CORS preflight), so it works through any proxy /
 * gateway in front of the API without server changes. Older backends ignore it.
 */
export const withClientContext = (url) => {
  try {
    const ctx = encodeURIComponent(JSON.stringify(getClientContext()));
    return `${url}${url.includes("?") ? "&" : "?"}ctx=${ctx}`;
  } catch (e) {
    return url;
  }
};
